import { useFlags as useLDFlags } from 'launchdarkly-react-client-sdk';
import { useContext } from 'react';
import { FeatureFlagOverrideContext } from '@/lib/providers/featureFlagOverrideContext';
import type { FlagKeys } from '@/lib/providers/featureFlagProvider';
import { isDevToolsEnabled } from '@/lib/utils/dev-tools/isDevToolsEnabled';

/**
 * Custom hook that wraps LaunchDarkly's useFlags and applies local overrides.
 * Overrides only apply in development/staging environments.
 * Falls back to LaunchDarkly flags if the override context is not available (e.g., in tests).
 *
 * Usage:
 *   const { myFlag } = useFlags<FlagKeys>();
 */
export const useFlags = <T extends FlagKeys = FlagKeys>(): T => {
  const ldFlags = useLDFlags<T>();
  // Try to get the override context (may not be available in tests)
  const context = useContext(FeatureFlagOverrideContext);

  // If dev tools are not enabled, just return LaunchDarkly flags
  if (!isDevToolsEnabled()) {
    return ldFlags;
  }

  // If context is not available, just return LaunchDarkly flags
  if (!context) {
    return ldFlags;
  }

  // In dev/staging with context available, apply overrides
  const { overrides } = context;

  // Merge LaunchDarkly flags with local overrides
  // Overrides take precedence
  return {
    ...ldFlags,
    ...overrides,
  } as T;
};

export const useFlagOverrides = (): Partial<FlagKeys> => {
  const context = useContext(FeatureFlagOverrideContext);
  if (!isDevToolsEnabled() || !context) {
    return {};
  }

  // In dev/staging with context available, apply overrides
  return context.overrides;
};
